import React from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useIsAdmin } from '@/hooks/useAccount';
import { useVoiceServiceContext } from '@/contexts/VoiceServiceContext';
import { NETWORK_WARNING_INFO, NetworkWarningName } from '@/services/voice';

/**
 * SettingsPanel - Account access and admin settings
 */
const SettingsPanel: React.FC = () => {
    const { t } = useTranslation('common');
    const isAdmin = useIsAdmin();
    const { provider, setProvider, activeNetworkWarnings, triggerNetworkWarning, clearNetworkWarning } = useVoiceServiceContext();

    return (
        <div className="absolute inset-0 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto min-h-0">
                <div className="w-full max-w-md mx-auto space-y-4 p-6">
                    {/* Account Button */}
                    <Button
                        variant="outline"
                        size="lg"
                        className="w-full justify-start gap-3 h-14"
                        asChild
                    >
                        <Link to="/account">
                            <User className="w-5 h-5" />
                            <span className="text-base">{t('navigation.account')}</span>
                        </Link>
                    </Button>

                    {/* Admin Panel - Only visible to admin users */}
                    {isAdmin && (
                        <div className="border border-border rounded-lg p-4 space-y-3">
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin</p>
                            <div className="space-y-1.5">
                                <p className="text-sm font-medium">Voice Provider</p>
                                <select
                                    className="w-full p-2 text-sm border rounded-md bg-background"
                                    value={provider}
                                    onChange={(e) => setProvider(e.target.value as 'twilio' | 'mock')}
                                >
                                    <option value="twilio">Twilio (live calls)</option>
                                    <option value="mock">Mock (simulated calls)</option>
                                </select>
                            </div>

                            {provider === 'mock' && (
                                <div className="pt-2 border-t border-border space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground">Simulate network conditions</p>
                                    {(Object.entries(NETWORK_WARNING_INFO) as [NetworkWarningName, typeof NETWORK_WARNING_INFO[NetworkWarningName]][]).map(([name, info]) => {
                                        const isActive = activeNetworkWarnings.includes(name)
                                        return (
                                            <div key={name} className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium leading-tight">{info.label}</p>
                                                    <p className="text-xs text-muted-foreground leading-tight truncate">{name}</p>
                                                </div>
                                                <Switch
                                                    checked={isActive}
                                                    onCheckedChange={(checked) =>
                                                        checked ? triggerNetworkWarning(name) : clearNetworkWarning(name)
                                                    }
                                                />
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPanel;
